import {
  Container,
  Divider,
  Stack,
  Typography,
  TextField,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { themeOrange, themeBlue } from "../../utils/colorTheme";
import CustomButton from "../../component/common/Button/Button";
import { MdSave } from "react-icons/md";
import { asyncStatus } from "../../utils/asyncStatus";
import { getAppSettings, saveAppSettings } from "../../services/AppSettingsService";
import { resetSaveStatus } from "../../store/slices/appSettingsSlice";

const DashboardAppSettings = () => {
  const dispatch = useDispatch();

  const {
    settings: savedSettings,
    get_settings_status,
    save_settings_status,
  } = useSelector((state) => state.appSettings);

  const [settings, setSettings] = useState({
    platformName: "Done",
    supportEmail: "support@donerightaway.com",
    supportPhone: "(123) 456-7890",
    commissionPercentage: "15",
    enableNotifications: true,
    enableMaintenance: false,
  });

  const isLoading = get_settings_status === asyncStatus.LOADING;
  const isSaving = save_settings_status === asyncStatus.LOADING;

  useEffect(() => {
    dispatch(getAppSettings());
  }, [dispatch]);

  useEffect(() => {
    if (savedSettings) {
      setSettings({
        platformName: savedSettings.platform_name || "Done",
        supportEmail: savedSettings.support_email || "support@donerightaway.com",
        supportPhone: savedSettings.support_phone || "(123) 456-7890",
        commissionPercentage: savedSettings.commission_percentage || "15",
        enableNotifications: savedSettings.enable_notifications === "true",
        enableMaintenance: savedSettings.enable_maintenance === "true",
      });
    }
  }, [savedSettings]);

  useEffect(() => {
    if (save_settings_status === asyncStatus.SUCCEEDED) {
      dispatch(resetSaveStatus());
    }
  }, [save_settings_status, dispatch]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = () => {
    dispatch(saveAppSettings(settings));
  };

  if (isLoading) {
    return (
      <Stack bgcolor={"#FAFAFA"} alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  return (
    <Stack bgcolor={"#FAFAFA"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        my={1}
      >
        <Typography className="mainHeading">App Settings</Typography>
        <Stack
          className="globleGradientBlue"
          sx={{ borderRadius: "10px", overflow: "hidden" }}
        >
          <CustomButton
            style={{
              backgroundColor: "transparent",
              fontSize: "16px",
              fontWeight: "500",
              paddingTop: "10px",
              paddingBottom: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              minWidth: 150,
            }}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <>
                <MdSave size={20} />
                <Typography sx={{ color: "white" }}>Save Settings</Typography>
              </>
            )}
          </CustomButton>
        </Stack>
      </Stack>
      <Divider />

      <Container
        sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}
      >
        <Stack my={3}>
          <Grid container spacing={3}>
            {/* General Settings */}
            <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "20px !important", mb: 3, color: themeBlue }}
                >
                  General Settings
                </Typography>

                <Stack gap={2.5}>
                  <TextField
                    label="Platform Name"
                    value={settings.platformName}
                    onChange={handleChange("platformName")}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                  <TextField
                    label="Support Email"
                    value={settings.supportEmail}
                    onChange={handleChange("supportEmail")}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                  <TextField
                    label="Support Phone"
                    value={settings.supportPhone}
                    onChange={handleChange("supportPhone")}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Commission Settings */}
            <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "20px !important", mb: 3, color: themeOrange }}
                >
                  Commission Settings
                </Typography>

                <Stack gap={2.5}>
                  <TextField
                    label="Platform Commission (%)"
                    value={settings.commissionPercentage}
                    onChange={handleChange("commissionPercentage")}
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                  <Typography className="mainPara" sx={{ color: "#666" }}>
                    This percentage will be deducted from pro earnings on each completed job.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            {/* System Settings */}
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "20px !important", mb: 3, color: "#666" }}
                >
                  System Settings
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableNotifications}
                          onChange={handleChange("enableNotifications")}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: themeOrange,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: themeOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Stack>
                          <Typography sx={{ fontWeight: 500 }}>
                            Push Notifications
                          </Typography>
                          <Typography className="mainPara" sx={{ color: "#666", fontSize: "12px" }}>
                            Enable push notifications for users and pros
                          </Typography>
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableMaintenance}
                          onChange={handleChange("enableMaintenance")}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#ef4444",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#ef4444",
                            },
                          }}
                        />
                      }
                      label={
                        <Stack>
                          <Typography sx={{ fontWeight: 500 }}>
                            Maintenance Mode
                          </Typography>
                          <Typography className="mainPara" sx={{ color: "#666", fontSize: "12px" }}>
                            Enable maintenance mode to temporarily disable the platform
                          </Typography>
                        </Stack>
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardAppSettings;
